import { useEffect, useState } from 'react';
import { getSearchParams, handleSearchParams } from '../../../../helpers/handleSearchParams';
import { getReportsEfficacy } from '../../../../services/reports/sellsService';
import { PAGE_LIMIT } from './constants';
import { colById } from './SeriesTable';

/**
 * Hook que dispara el fetch del report cada vez que cambia el estado
 * de filtros o sort. Acepta `filterState` (todo lo que devuelve
 * `useEfficacyFilters`) y expone el estado de sort + el dato.
 *
 * También sincroniza `sortBy` / `sortDir` con la URL y delega en
 * `filterState.persistInUrl()` para el resto.
 */
export const useEfficacyData = (filterState) => {
  const [data, setData] = useState(null);

  // Sort: por defecto familia ASC (visible al cargar). Numérico → DESC
  // por defecto (lo más relevante primero).
  const [sortBy, setSortBy] = useState(() => getSearchParams('sortBy') || 'family');
  const [sortDir, setSortDir] = useState(() => (getSearchParams('sortDir') === 'desc' ? 'desc' : 'asc'));

  const handleSort = (columnId) => {
    if (columnId === sortBy) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
      return;
    }
    setSortBy(columnId);
    const col = colById(columnId);
    setSortDir(col?.align === 'left' ? 'asc' : 'desc');
  };

  const {
    season,
    year,
    depositsValue,
    depositRolesValue,
    depositGroupsValue,
    familyQuery,
    filters,
    ready,
    validFilters,
    persistInUrl,
  } = filterState;

  useEffect(() => {
    if (!ready) return;
    persistInUrl();
    handleSearchParams('sortBy', sortBy);
    handleSearchParams('sortDir', sortDir);

    const args = { season, year, page: 1, limit: PAGE_LIMIT, sortBy, sortDir, ...validFilters };
    if (depositsValue) args.deposits = depositsValue;
    if (depositRolesValue) args.depositRoles = depositRolesValue;
    if (depositGroupsValue) args.depositGroups = depositGroupsValue;
    if (familyQuery) args.family = familyQuery;

    getReportsEfficacy(args)
      .then((r) => setData(r.data))
      .catch(() => setData(null));
    // `persistInUrl` cambia identidad cada render pero su efecto es
    // idempotente (escribir los mismos valores en URL). Lo dejamos
    // fuera de las deps para no disparar fetch extra; las deps reales
    // son los valores listados abajo.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [season, year, depositsValue, depositRolesValue, depositGroupsValue, familyQuery, filters, ready, sortBy, sortDir, validFilters]);

  return { data, sortBy, sortDir, handleSort };
};
